import { z } from "zod";

import { looseUuid } from "@thalamos/common";

import {
  AvailableLanguagesSchema,
  type AvailableLanguages,
} from "./availableLanguages.js";
import type { OrganisationIdUnbranded } from "./organisation.js";
import {
  WorkflowVersionSchema,
  type WorkflowVersion,
} from "./workflowVersion.js";

export type OrganisationConfigurationJsonUnbranded = {
  language?: AvailableLanguages;
  associatedEmailDomains?: string[];
  workflowVersion?: WorkflowVersion;

  // Used to control how often we notify users through email or in-app
  // that they need to update an incident
  intervalBetweenUpdateIncidentEmailsMinutes?: number;
  intervalBetweenUpdateIncidentAppPromptMinutes?: number;
  isTrainingOrganisation?: boolean;

  patientReconciliationEnabled?: boolean;
  amberS136ThresholdHours?: number;
  redS136ThresholdHours?: number;

  mentalCapacityActEnabled?: boolean;
  snapshotDownloadEnabled?: boolean;
  incidentSummaryViewEnabled?: boolean;

  // Out-of-force-area Place of Safety warning
  // If non-empty, shows warning when selected POS is outside these postcode prefixes
  forceAreaPostcodePrefixes?: string[];

  // Superset dashboard embedding configuration
  embedDashboard?: {
    dashboardId: string;
    embeddedUuid: string;
  };

  // Power BI dashboard embedding — per-org toggle
  embedPowerBiDashboard?: boolean;

  // Officer lookup for "created on behalf of" field.
  // When enabled, the shoulder-number textbox is replaced with a searchable officer
  // typeahead that requires the user to confirm the selected officer's details.
  // Defaults to false — must be explicitly enabled by a Thalamos administrator.
  officerLookupEnabled?: boolean;
};

const emailDomainRegex = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const OrganisationConfigurationJsonSchema = z.object({
  language: AvailableLanguagesSchema.optional(),
  associatedEmailDomains: z
    .array(
      z
        .string()
        .regex(emailDomainRegex, {
          message: "Invalid email domain",
        })
        .max(255),
    )
    .optional(),
  workflowVersion: WorkflowVersionSchema.optional(),
  intervalBetweenUpdateIncidentEmailsMinutes: z
    .number()
    .int()
    .min(0)
    .max(60 * 24 * 2) // Two days - guards against absurd values
    .optional(),
  intervalBetweenUpdateIncidentAppPromptMinutes: z
    .number()
    .int()
    .min(0)
    .max(60 * 24 * 2) // Two days - guards against absurd values
    .optional(),
  isTrainingOrganisation: z.boolean().optional(),
  patientReconciliationEnabled: z.boolean().optional(),
  amberS136ThresholdHours: z.number().int().min(1).max(168).optional(),
  redS136ThresholdHours: z.number().int().min(1).max(168).optional(),
  mentalCapacityActEnabled: z.boolean().optional(),
  snapshotDownloadEnabled: z.boolean().optional(),
  incidentSummaryViewEnabled: z.boolean().optional(),
  forceAreaPostcodePrefixes: z.array(z.string().min(1).max(2)).optional(),
  embedDashboard: z
    .object({
      // Superset uses numeric IDs for dashboards; this must remain numeric to match its internal dashboard ID format.
      dashboardId: z.string().regex(/^\d+$/, "Must be a numeric string"),
      embeddedUuid: looseUuid(),
    })
    .optional(),
  embedPowerBiDashboard: z.boolean().optional(),
  officerLookupEnabled: z.boolean().optional(),
});

export type OrganisationConfigurationJson = z.infer<
  typeof OrganisationConfigurationJsonSchema
>;

export const OrganisationConfigurationSchema = z.object({
  organisationId: looseUuid(),
  configuration: OrganisationConfigurationJsonSchema,
});

export type OrganisationConfiguration = z.infer<
  typeof OrganisationConfigurationSchema
>;

export type OrganisationConfigurationHierarchyUnbranded = {
  organisationId: OrganisationIdUnbranded;
  managingOrganisationId: OrganisationIdUnbranded | null;
  name: string;
  configuration?: Partial<OrganisationConfigurationJsonUnbranded>;
  amberS136ThresholdHours?: number;
  redS136ThresholdHours?: number;
}[];

export const OrganisationConfigurationHierarchySchema = z.array(
  z.object({
    organisationId: looseUuid(),
    managingOrganisationId: looseUuid().nullable(),
    name: z.string().max(255),
    configuration: OrganisationConfigurationJsonSchema.optional(),
  }),
);

export type OrganisationConfigurationHierarchy = z.infer<
  typeof OrganisationConfigurationHierarchySchema
>;

export const defaultConfiguration: OrganisationConfigurationJson = {
  workflowVersion: WorkflowVersionSchema.enum.BTP_1_0,
  language: AvailableLanguagesSchema.enum.BritishTransportPolice,
  associatedEmailDomains: [],
  isTrainingOrganisation: false,
  mentalCapacityActEnabled: false,
  patientReconciliationEnabled: false,
  incidentSummaryViewEnabled: false,
  forceAreaPostcodePrefixes: [],
};

export function mergeConfigurations(
  organisationId: string | null,
  configurations: OrganisationConfigurationHierarchy,
): OrganisationConfigurationJson {
  const configuration = configurations.find(
    (c) => c.organisationId === organisationId,
  );

  if (!configuration) {
    return defaultConfiguration;
  }

  if (!configuration.managingOrganisationId) {
    return { ...defaultConfiguration, ...configuration.configuration };
  }

  const parentConfig = mergeConfigurations(
    configuration.managingOrganisationId,
    configurations,
  );

  return {
    ...parentConfig,
    ...configuration.configuration,
  };
}
